/**
 * use-tenant-people — TanStack Query hooks dla F10 T10.8 (Users page) + T10.9
 * (Employees page).
 *
 * - useUserDetail(userId) — pojedynczy user (drawer Users page, T10.8).
 * - useEmployeesList(filters) — lista pracownikow z filtrami (T10.9).
 * - useEmployeeDetail(employeeId) — pojedynczy pracownik z PESEL/dowod (gated
 *   permission po stronie backendu, plain text gdy `hr.employees.read_pesel`,
 *   maska gdy bez).
 * - useOrgUnitsForCompany(companyId) — flat lista jednostek organizacyjnych firmy
 *   (filtr pracownikow + modal Create/Edit). Disabled gdy brak companyId.
 *   (F24 Pakiet 0, dawniej useDepartmentsForCompany — endpoint `/departments` →
 *   `/org-units`.)
 *
 * Lista userow (`useUsersList`) i lista firm (`useCompanies`) — istnieja juz w
 * `use-permissions.ts` (T10.10), uzywamy ich bezposrednio. NIE duplikujemy.
 * Lista szablonow rol (`useRoleTemplates`) — j/w.
 *
 * staleTime 0 dla detail — chcemy widziec efekt mutacji natychmiast.
 * staleTime 30s dla list (org-units / employees list) — analogiczne do
 * use-permissions, na auto-save.
 */

import type { EmployeeIdDocumentType, EmploymentType, RecordLanguage } from '@saas/shared';
import { useQuery } from '@tanstack/react-query';

import type { ListResponseMeta } from '@/components/configurable-table/useServerListControls';
import { api } from '@/lib/api';
import type { ExtRefLabels } from '@/lib/ext-list-columns';
import { retryUnlessForbidden } from '@/lib/query';

// ============================================================================
// Types
// ============================================================================

export interface UserCompanyDetailItem {
  id: string;
  legalName: string;
  shortName: string | null;
  isManager: boolean;
  /** Wszystkie role osoby w firmie, w kolejności — pierwsza = główna (ADR-0103). */
  roles?: Array<{ id: string; name: string }>;
  /** Rola główna (pierwsza z `roles`) — dla ekranów sprzed wielu ról. */
  roleTemplateId: string | null;
  roleTemplateName: string | null;
}

/**
 * Pracownik powiązany z kontem (`GET /api/users/:id` → `user.employee`). `companyAccessible=false`
 * = konto nie ma dostępu do firmy tego pracownika (powiązanie istnieje, ale jest „martwe").
 */
export interface UserEmployeeSummary {
  id: string;
  firstName: string;
  lastName: string;
  companyId: string;
  /** Skrócona albo pełna nazwa firmy; null tylko gdy firmy pracownika nie ma w bazie. */
  companyName: string | null;
  isActive: boolean;
  companyAccessible: boolean;
}

export interface UserDetail {
  id: string;
  email: string;
  displayName: string | null;
  userType: 'human' | 'api' | 'service' | 'impersonation';
  status: 'active' | 'suspended' | 'invited';
  isSuperUser: boolean;
  employeeId: string | null;
  /** Podsumowanie powiązanego pracownika (null = brak powiązania). */
  employee: UserEmployeeSummary | null;
  defaultCompanyId: string | null;
  avatarPath: string | null;
  language: RecordLanguage;
  lastLoginAt: string | null;
  invitedAt: string | null;
  suspendedAt: string | null;
  suspendedReason: string | null;
  createdAt: string;
  updatedAt: string;
  gcrecord: string | null;
  hasAccount: boolean;
  totpEnabled: boolean;
  companies: UserCompanyDetailItem[];
  /** Grupy rekordu — kody słownika `user_groups`, wiele na rekord (migracja 0117). */
  groups: string[];
  /** Kategoria rekordu — jedna, z `record_categories` (kartoteka_key='users', migracja 0130). */
  categoryId: string | null;
  /** Surowe wartości pól dodatkowych — dołączone przez serwer do odpowiedzi GET rekordu. */
  extValues?: Record<string, unknown>;
}

export interface UserDetailResponse {
  user: UserDetail;
}

/**
 * Odpowiedź `POST /api/users/:id/restore`. `employeeUnlinked=true` = konto przywrócono BEZ
 * powiązania z pracownikiem (ten pracownik ma już inne żywe konto).
 */
export interface UserRestoreResponse {
  employeeUnlinked: boolean;
}

// ============================================================================
// Query keys
// ============================================================================

export const USER_DETAIL_KEY = (userId: string) => ['users', userId, 'detail'] as const;

// ============================================================================
// Hooks
// ============================================================================

export function useUserDetail(userId: string | null) {
  return useQuery<UserDetailResponse>({
    queryKey: userId ? USER_DETAIL_KEY(userId) : ['users', 'none', 'detail'],
    queryFn: async () => {
      const res = await api.get<UserDetailResponse>(`/users/${userId}`);
      return res.data;
    },
    enabled: !!userId,
    staleTime: 0,
  });
}

/**
 * Kandydat na pracownika konta (`GET /api/users/:id/employee-candidates`). `linkedUser` = INNE
 * konto, które już trzyma tego pracownika (kandydat jest wtedy niedostępny do wyboru).
 */
export interface UserEmployeeCandidate {
  id: string;
  firstName: string;
  lastName: string;
  companyId: string;
  companyName: string | null;
  isActive: boolean;
  linkedUser: { id: string; displayName: string | null; username: string | null } | null;
}

export interface UserEmployeeCandidatesResponse {
  candidates: UserEmployeeCandidate[];
}

export const USER_EMPLOYEE_CANDIDATES_KEY = (userId: string, search: string) =>
  ['users', userId, 'employee-candidates', search] as const;

/**
 * Kandydaci na pracownika konta z wyszukiwaniem po stronie serwera. `enabled` steruje
 * wywołujący (lista pobierana dopiero po otwarciu pola — endpoint wymaga uprawnienia edycji
 * użytkowników, więc nie wołamy go w trybie podglądu).
 */
export function useUserEmployeeCandidates(userId: string | null, search: string, enabled: boolean) {
  return useQuery<UserEmployeeCandidatesResponse>({
    queryKey: USER_EMPLOYEE_CANDIDATES_KEY(userId ?? 'none', search),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      const qs = params.toString();
      const res = await api.get<UserEmployeeCandidatesResponse>(
        `/users/${userId}/employee-candidates${qs ? `?${qs}` : ''}`,
      );
      return res.data;
    },
    enabled: enabled && !!userId,
    staleTime: 15_000,
    // 403 = brak prawa edycji użytkowników — ponawianie tylko opóźnia komunikat „brak uprawnień".
    retry: retryUnlessForbidden,
    // Zmiana frazy = nowy klucz zapytania; bez tego lista pustoszałaby (flash „Brak wyników") do
    // czasu odpowiedzi — zostaje poprzednia lista („Ładowanie…" widać tylko przy pustej). Tylko w
    // obrębie TEGO SAMEGO konta: kandydaci innego konta mają inną zajętość i inne firmy.
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[1] === (userId ?? 'none') ? previous : undefined,
  });
}

// ============================================================================
// T10.9 — Employees
// ============================================================================

/**
 * Wiersz na listingu pracownikow. PESEL/dowod ZAWSZE masked (`***********` /
 * `***`) — backend nigdy nie zwraca plain w listingu (security policy).
 */
export interface EmployeeListItem {
  id: string;
  firstName: string;
  lastName: string;
  middleName: string | null;
  birthDate: string | null;
  taxId: string | null;
  idDocumentType: EmployeeIdDocumentType | null;
  pesel: string | null; // masked '***********' lub null
  idDocumentNumber: string | null; // masked '***' lub null
  companyId: string;
  orgUnitId: string | null;
  /** Nazwa jednostki z `org_units` (LEFT JOIN w deskryptorze listy, mirror `categoryName`). */
  orgUnitName?: string | null;
  /** Kod pozycji słownika `employee_positions` (F24 Pakiet 0, dawniej wolny tekst `position`). */
  positionCode: string | null;
  specialization: string | null;
  employmentType: EmploymentType | null;
  employmentStartDate: string | null;
  employmentEndDate: string | null;
  internalCode: string | null;
  managerEmployeeId: string | null;
  phone: string | null;
  mobile: string | null;
  workEmail: string | null;
  isActive: boolean;
  hasAccount: boolean;
  createdAt: string;
  /** Nazwa kategorii z `record_categories` (LEFT JOIN w deskryptorze listy). Kolumna RĘCZNA. */
  categoryName?: string | null;
  /** Niepuste tylko gdy lista pobrana z `?includeDeleted=true`. */
  gcrecord: string | null;
  /**
   * Wartości pól dodatkowych (ext) — obecne TYLKO gdy backend odpowiada w trybie
   * projekcji `?columns=` i zażądano ≥1 aktywnego pola ext (kontrakt
   * `GET /api/employees?columns=`). Konsumenci hooka bez `columns` nie dostają `ext`.
   */
  ext?: Record<string, unknown>;
}

export interface EmployeesListResponse {
  employees: EmployeeListItem[];
  total: number;
  limit: number;
  offset: number;
  /** Etykiety pól `reference` (sibling do wierszy) — obecne gdy `?columns=` obejmuje
   *  przynajmniej jedno takie pole. Patrz `ExtRefLabels` (`@/lib/ext-list-columns`). */
  extRefLabels?: ExtRefLabels;
  /**
   * Slugi kolumn CORE sortowalnych serwerowo (silnik list) — patrz `useServerListControls`.
   * Backend po migracji na silnik list dokłada ten obiekt do odpowiedzi; pole ADDYTYWNE
   * (konsumenci spoza `Employees.tsx` mogą je ignorować bez zmiany zachowania).
   */
  meta?: ListResponseMeta;
}

export interface EmployeeDetailAccount {
  userId: string;
  email: string;
  status: 'active' | 'suspended' | 'invited';
}

/**
 * Przełożony WYNIKOWY (F24 Pakiet 0) — kto rzeczywiście widnieje jako przełożony
 * pracownika, po rozstrzygnięciu nadpisania (`managerEmployeeId`) vs kierownika
 * jednostki organizacyjnej z drzewa. `source: 'override'` = z jawnego nadpisania,
 * `'unit'` = wyprowadzony z `org_units.managerEmployeeId`.
 *
 * OPCJONALNE (backend może jeszcze nie zwracać tego pola — równoległy task) —
 * renderuj blok „Przełożony wynikowy" WYŁĄCZNIE gdy klucz jest obecny, żeby UI
 * działało identycznie przed i po wdrożeniu tamtej zmiany.
 */
export interface EmployeeEffectiveSupervisor {
  employeeId: string;
  fullName: string;
  source: 'override' | 'unit';
}

/**
 * Szczegoly pracownika. PESEL/dowod plain text gdy user wolajacy ma permission
 * `hr.employees.read_pesel`, w przeciwnym razie maska + flagi `peselMasked` /
 * `idDocumentMasked` informuja UI ze pole istnieje ale jest ukryte.
 */
export interface EmployeeDetail {
  id: string;
  firstName: string;
  lastName: string;
  middleName: string | null;
  birthDate: string | null;
  pesel: string | null;
  peselMasked: boolean;
  taxId: string | null;
  idDocumentType: EmployeeIdDocumentType | null;
  idDocumentNumber: string | null;
  idDocumentMasked: boolean;
  companyId: string;
  orgUnitId: string | null;
  /** Kod pozycji słownika `employee_positions` (F24 Pakiet 0, dawniej wolny tekst `position`). */
  positionCode: string | null;
  specialization: string | null;
  employmentType: EmploymentType | null;
  employmentStartDate: string | null;
  employmentEndDate: string | null;
  internalCode: string | null;
  /** Nadpisanie przełożonego (wyjątek od drzewa jednostek) — puste = wynika z jednostki. */
  managerEmployeeId: string | null;
  phone: string | null;
  mobile: string | null;
  workEmail: string | null;
  isActive: boolean;
  /** Grupy rekordu — kody słownika `employee_groups`, wiele na rekord (filtr listy). */
  groups: string[];
  /** Kategoria rekordu — pojedynczy wybór z `record_categories` (kartoteka_key='employees'). */
  categoryId: string | null;
  notes: string | null;
  /** Podgląd integracji Symfonia (read-only) — źródło + pozycja/status PO STRONIE Symfonii,
   *  ODRĘBNE od naszego `isActive`. Per firma (rekord pracownika jest per firma). */
  externalSource?: string | null;
  symfoniaExternalId?: string | null;
  symfoniaPosition?: number | null;
  symfoniaActive?: boolean | null;
  createdAt: string;
  updatedAt: string;
  gcrecord: string | null;
  account: EmployeeDetailAccount | null;
  /** Patrz `EmployeeEffectiveSupervisor` — obecne TYLKO gdy backend już to wylicza. */
  effectiveSupervisor?: EmployeeEffectiveSupervisor | null;
  /** Surowe wartości pól dodatkowych — dołączone przez serwer do odpowiedzi GET rekordu. */
  extValues?: Record<string, unknown>;
}

export interface EmployeeDetailResponse {
  employee: EmployeeDetail;
}

/** Rodzaj jednostki organizacyjnej (F24 Pakiet 0) — mirror `ORG_UNIT_KINDS` backendu. */
export type OrgUnitKind = 'department' | 'division' | 'branch';

/**
 * Trzy rodzaje jednostki, w kolejności prezentacji w selectach. Pole `org_units.kind`
 * w katalogu (`system_module_fields`) jest typu `select` BEZ `dictionaryCode` (seed
 * backendu, F24 Pakiet 0) — etykiety NIE pochodzą ze słownika, front trzyma stałą mapę
 * i tłumaczy przez klucze `tenant.settings.orgStructure.kind.<kind>` (Dział/Pion/Oddział).
 * Jedno źródło — reużywane w edytorze jednostki, badge'u na drzewie i organigramie.
 */
export const ORG_UNIT_KINDS: readonly OrgUnitKind[] = ['department', 'division', 'branch'];

export interface OrgUnitItem {
  id: string;
  companyId: string;
  parentId: string | null;
  name: string;
  /** Kod MPK (miejsce powstawania kosztów), opcjonalny. */
  code: string | null;
  kind: OrgUnitKind;
  managerEmployeeId: string | null;
  /** Imię i nazwisko kierownika — wyliczone przez backend (LEFT JOIN employees). */
  managerName: string | null;
  isActive: boolean;
  sortOrder: number | null;
  gcrecord: string | null;
}

export interface OrgUnitsResponse {
  orgUnits: OrgUnitItem[];
}

export interface OrgUnitTreeNode {
  id: string;
  parentId: string | null;
  name: string;
  sortOrder: number | null;
  kind: OrgUnitKind;
  managerEmployeeId: string | null;
  managerName: string | null;
  children: OrgUnitTreeNode[];
}

export interface OrgUnitTreeResponse {
  tree: OrgUnitTreeNode[];
}

export interface EmployeesListFilters {
  companyId?: string;
  orgUnitId?: string;
  employmentType?: EmploymentType;
  isActive?: boolean;
  hasAccount?: boolean;
  includeDeleted?: boolean;
  /**
   * Filtr po GRUPACH ze słownika `employee_groups` (chipy nad listą). Wiele kodów = OR
   * („ma którąkolwiek z"). Wysyłane jako powtórzony parametr `groups`.
   */
  groups?: string[];
  /** F12 PR2 cz.5.1 — backend search (multi-word AND-words OR-columns). */
  search?: string;
  /** F12 PR2 cz.5.1 — CSV slugów kolumn po stronie wywołującego (np. ['first_name','last_name']). */
  searchColumns?: string[];
  /** F12 PR2 cz.5.1 — slug pola sortującego (whitelist backendu). */
  sort?: string;
  /** F12 PR2 cz.5.1 — kierunek sortu. */
  order?: 'asc' | 'desc';
  /** Kreator Listy — filtry per kolumna (popup nagłówka + szybkie filtry) jako serializowany JSON array. */
  headerFilters?: string;
  /** Kreator kolumn ext — CSV slugów widocznych kolumn dla `?columns=` projekcji. */
  columns?: string;
  limit?: number;
  offset?: number;
}

// ============================================================================
// Query keys (employees)
// ============================================================================

export const EMPLOYEES_LIST_KEY = (filters: EmployeesListFilters) =>
  ['employees', 'list', filters] as const;
export const EMPLOYEE_DETAIL_KEY = (employeeId: string) =>
  ['employees', employeeId, 'detail'] as const;
export const ORG_UNITS_BY_COMPANY_KEY = (companyId: string) =>
  ['org-units', companyId, 'list'] as const;

// ============================================================================
// Hooks (employees)
// ============================================================================

/**
 * Parametry zapytania listy pracowników — JEDNO źródło dla `useEmployeesList` i eksportu widoku
 * listy (E16, `/list-export/employees`), żeby plik miał dokładnie te same filtry co ekran.
 */
export function buildEmployeesListParams(filters: EmployeesListFilters): Record<string, string> {
  const params: Record<string, string> = {};
  if (filters.companyId) params.companyId = filters.companyId;
  if (filters.orgUnitId) params.orgUnitId = filters.orgUnitId;
  if (filters.employmentType) params.employmentType = filters.employmentType;
  if (filters.isActive !== undefined) params.isActive = String(filters.isActive);
  if (filters.hasAccount !== undefined) params.hasAccount = String(filters.hasAccount);
  // Silnik list rozpoznaje `withDeleted`/`includeArchived`/`include_deleted` — NIE camelCase
  // `includeDeleted` (którego używał dawny ręczny handler). Wysyłamy `includeArchived`, inaczej
  // przełącznik „Pokaż archiwalne" byłby po cichu gubiony (silnik zawsze filtrowałby gcrecord).
  if (filters.includeDeleted) params.includeArchived = 'true';
  // Lista po przecinku, NIE tablica: axios serializowałby tablicę jako `groups[]=a&groups[]=b`,
  // czego backend (`searchParams.getAll('groups')`) nie widzi. Parser backendu rozbija po przecinku.
  if (filters.groups && filters.groups.length > 0) params.groups = filters.groups.join(',');
  if (filters.search) params.search = filters.search;
  if (filters.searchColumns && filters.searchColumns.length > 0) {
    params.searchColumns = filters.searchColumns.join(',');
  }
  if (filters.sort) params.sort = filters.sort;
  if (filters.order) params.order = filters.order;
  if (filters.headerFilters) params.headerFilters = filters.headerFilters;
  if (filters.columns) params.columns = filters.columns;
  if (filters.limit !== undefined) params.limit = String(filters.limit);
  if (filters.offset !== undefined) params.offset = String(filters.offset);
  return params;
}

export function useEmployeesList(filters: EmployeesListFilters = {}) {
  return useQuery<EmployeesListResponse>({
    queryKey: EMPLOYEES_LIST_KEY(filters),
    queryFn: async () => {
      const params = buildEmployeesListParams(filters);
      const res = await api.get<EmployeesListResponse>('/employees', { params });
      return res.data;
    },
    staleTime: 30_000,
  });
}

export function useEmployeeDetail(employeeId: string | null) {
  return useQuery<EmployeeDetailResponse>({
    queryKey: employeeId ? EMPLOYEE_DETAIL_KEY(employeeId) : ['employees', 'none', 'detail'],
    queryFn: async () => {
      const res = await api.get<EmployeeDetailResponse>(`/employees/${employeeId}`);
      return res.data;
    },
    enabled: !!employeeId,
    staleTime: 0,
  });
}

/**
 * Lista jednostek organizacyjnych (flat) dla wybranej firmy. Zwraca pusty array
 * gdy `companyId` null/undefined (`enabled: false` — query nie startuje, ale
 * komponent dostaje stabilny `data?.orgUnits`).
 *
 * Jednostki maja FK do `company_id` — bez wybranej firmy nie ma sensu pytac.
 * (F24 Pakiet 0, dawniej useDepartmentsForCompany.)
 */
export function useOrgUnitsForCompany(companyId: string | null | undefined) {
  return useQuery<OrgUnitsResponse>({
    queryKey: companyId ? ORG_UNITS_BY_COMPANY_KEY(companyId) : ['org-units', 'none'],
    queryFn: async () => {
      const res = await api.get<OrgUnitsResponse>(`/companies/${companyId}/org-units`);
      return res.data;
    },
    enabled: !!companyId,
    staleTime: 30_000,
  });
}

/** Drzewo jednostek organizacyjnych (nested `children`) dla wybranej firmy. */
export function useOrgUnitsTree(companyId: string | null | undefined) {
  return useQuery<OrgUnitTreeResponse>({
    queryKey: companyId
      ? [...ORG_UNITS_BY_COMPANY_KEY(companyId), 'tree']
      : ['org-units', 'none-tree'],
    queryFn: async () => {
      const res = await api.get<OrgUnitTreeResponse>(`/companies/${companyId}/org-units/tree`);
      return res.data;
    },
    enabled: !!companyId,
    staleTime: 30_000,
  });
}

/**
 * Buduje opcje pickera z listy FLAT jednostek, posortowane top-down po drzewie
 * (root najpierw, potem dzieci) z etykietą wciętą wg głębokości — „indented names"
 * z briefu F24 Pakiet 0. Działa na płaskiej odpowiedzi `GET /org-units` (bez
 * dodatkowego zapytania po `/tree`) — wystarczające dla pickerów (nazwa +
 * hierarchia), pełne drzewo z rozwijaniem jest w ekranie „Struktura".
 * Jednostka o `excludeId` oraz jej potomkowie są pomijane (np. przy przenoszeniu
 * jednostki — nie może stać się własnym rodzicem/potomkiem).
 */
export function buildIndentedOrgUnitOptions(
  units: OrgUnitItem[],
  excludeId?: string | null,
): Array<{ value: string; label: string }> {
  const byParent = new Map<string | null, OrgUnitItem[]>();
  for (const u of units) {
    const list = byParent.get(u.parentId) ?? [];
    list.push(u);
    byParent.set(u.parentId, list);
  }
  for (const list of byParent.values()) {
    list.sort((a, b) => {
      const ao = a.sortOrder ?? Number.MAX_SAFE_INTEGER;
      const bo = b.sortOrder ?? Number.MAX_SAFE_INTEGER;
      if (ao !== bo) return ao - bo;
      return a.name.localeCompare(b.name);
    });
  }

  const excludedIds = new Set<string>();
  if (excludeId) {
    const collect = (id: string) => {
      excludedIds.add(id);
      for (const child of byParent.get(id) ?? []) collect(child.id);
    };
    collect(excludeId);
  }

  const result: Array<{ value: string; label: string }> = [];
  const walk = (parentId: string | null, depth: number) => {
    for (const u of byParent.get(parentId) ?? []) {
      if (excludedIds.has(u.id)) continue;
      result.push({ value: u.id, label: `${'—'.repeat(depth)}${depth > 0 ? ' ' : ''}${u.name}` });
      walk(u.id, depth + 1);
    }
  };
  walk(null, 0);
  return result;
}
